
// Core imports
import React, { useEffect, useState, Fragment } from 'react';
import { useParams, Link as NavigationLink } from 'react-router-dom';
import { t } from 'i18next';
import PropTypes from 'prop-types';

// Component Imports
import { EllipsisOutlined } from '@ant-design/icons';
import { Button, Collapse, Empty, Row, Spin, Typography } from 'antd';
import { ExternalLinkIcon } from '../Icons/ExternalLinkIcon';

// Misc imports
import { useLazyFetchCrmIntegrationsSubscriberQuery } from '../../store/integrations/integrationsApi';
import { useLazyFetchBotQuery } from '../../store/bots/botsApi';
import { NAV_BOT } from '../../constants/navigation';
import { INTEGRATION_CATEGORY, INTEGRATION_TYPE } from '../../constants/integrations';
import replaceUrlParams from '../../utils/string/replaceUrlParams';

const CrmIntegrationsPanel = ({ conversation }) => {
  const { orgId } = useParams();
  const [contactProperties, setContactProperties] = useState(null);
  const [contactLinkInfo, setContactLinkInfo] = useState(null);
  const [fetchCrmIntegrationSubscriber] = useLazyFetchCrmIntegrationsSubscriberQuery();
  const [fetchBot] = useLazyFetchBotQuery();
  const [crmIntegration, setCrmIntegration] = useState(null);

  const getCrmIntegrationFromPlugins = (plugins) => plugins?.filter(plugin => plugin?.integration?.category === INTEGRATION_CATEGORY.CRM)?.[0]?.integration;

  useEffect(() => {
    return () => {
      setContactProperties(null);
      setContactLinkInfo(null);
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { plugins } = await fetchBot({ orgId, tenantId: conversation.tenantId }).unwrap();
        const crmIntegrationInfo = getCrmIntegrationFromPlugins(plugins);
        setCrmIntegration(crmIntegrationInfo);
      } catch (ignore) {
        setCrmIntegration({});
      }
    })();
  }, [conversation?.tenantId]);

  useEffect(() => {
    if (!conversation) {
      return;
    }

    const getContactLinkInfo = async (entityProperties) => {
      try {
        // Get integrations from bot to extract link template (if exists) as well as implementation type
        if (crmIntegration?.type && crmIntegration?.contactLinkTemplate) {
          let link = null;
          let friendlyName = null;
          let setInfo = true;
          switch (crmIntegration.type) {
            case INTEGRATION_TYPE.MICROSOFT_DYNAMICS_CRM: {
              const contactIdProperty = entityProperties?.find(entityProperty => entityProperty.dataPropertyPath === 'contactid');
              link = crmIntegration?.contactLinkTemplate?.replace('{{contactid}}', contactIdProperty?.value);
              friendlyName = 'Microsoft Dynamics';
              break;
            }
            default: {
              setInfo = false;
            }
          }
          if (setInfo) {
            setContactLinkInfo({ type: friendlyName, link });
          }
        } else {
          setContactLinkInfo({});
        }
      } catch (ignore) {
        setContactLinkInfo({});
      }
    };

    const getContactInfo = async () => {
      try {

        if(!crmIntegration) {
          setContactProperties([]);
          setContactLinkInfo({});
          return;
        }
        // Contact properties is an array of the contact's entity properties
        const { data } = await fetchCrmIntegrationSubscriber({ orgId, tenantId: conversation.tenantId, handle: conversation.platformUserId }).unwrap();
        setContactProperties(data.entityProperties);

        // No need to continue if previous request failed
        getContactLinkInfo(data.entityProperties);
      } catch (ignore) {
        setContactProperties([]);
        setContactLinkInfo({});
      }
    };

    getContactInfo();

    return () => {
      setContactProperties(null);
    };
  }, [conversation?.id, crmIntegration]);

  const renderLink = () => {
    if (contactLinkInfo?.type && contactLinkInfo?.link) {
      return (
        <a href={contactLinkInfo?.link} rel="noreferrer" target="_blank" className="fw-bold fs-sm">
          {'View in ' + contactLinkInfo?.type}
          <ExternalLinkIcon className="ml-1" />
        </a>
      );
    }
  };

  const renderCrmInfo = () => {
    if (contactProperties === null || contactLinkInfo == null) {
      return (
        <Row
          align="middle"
          justify="center"
          className="p-3 text-center"
        >
          <Spin />
        </Row>
      );
    }

    if (!crmIntegration) {
      return (
        <Row className="m-5">
          <Typography.Text className="text-muted">
            {t('The CRM add-on has not yet been set up. To activate it, ')}
            <NavigationLink
              to={replaceUrlParams(NAV_BOT, { tenantId: conversation?.tenantId, orgId }) + '?view=plugins'}
              rel="noopener noreferrer"
            >
              {t('go to the plugins section')}
            </NavigationLink>
          </Typography.Text>
        </Row>
      );
    }

    if (Array.isArray(contactProperties) && contactProperties.length === 0) {
      return (
        <Empty className="text-muted fs-sm fw-bold" description={t('No CRM data found')}/>
      );
    }

    // Else, render the contact properties
    return (
      <Fragment key="contactInfo">
        {
          contactProperties.filter(field => field.displayName).map((field, index) => {
            if (field.type === 'LOOKUP') {
              const { lookupEntityProperties } = field;
              return (
                <Collapse
                  ghost
                  key={index}
                  className="ant-collapse-p-0"
                >
                  <Collapse.Panel
                    key={index}
                    className="mb-1"
                    showArrow={false}
                    header={
                      <Row
                        wrap={false}
                        align="middle"
                      >
                        <Typography.Text
                          className="fw-bold fs-sm mr-1 text-muted white-space-nowrap"
                        >
                          {field.displayName}:
                        </Typography.Text>

                        <Button
                          className="fs-sm bg-layout word-break-break-all"
                          type="link"
                          size="small"
                        >
                          <Typography.Text
                            ellipsis
                            className="text-inherit"
                            style={{ maxWidth: '22ch' }}
                          >
                            {field.displayName}
                          </Typography.Text>

                          <EllipsisOutlined className="fs-lg" style={{ verticalAlign: -3 }} />
                        </Button>
                      </Row>
                    }
                  >
                    {
                      lookupEntityProperties?.map(nestedField => (
                        <Row
                          wrap={false}
                          align="middle"
                          className="pl-3 mb-2"
                        >
                          <Typography.Text
                            className="fw-bold fs-sm mr-1 text-muted white-space-nowrap"
                          >
                            {nestedField.displayName}:
                          </Typography.Text>

                          <Typography.Text
                            className="fs-sm word-break-break-all"
                          >
                            {nestedField.value || '-'}
                          </Typography.Text>
                        </Row>
                      ))
                    }
                  </Collapse.Panel>
                </Collapse>
              );
            }

            return (
              <Row
                wrap={false}
                align="middle"
                className="mb-2"
              >
                <Typography.Text className="fw-bold fs-sm mr-1 text-muted white-space-nowrap">
                  {field.displayName}:
                </Typography.Text>

                <Typography.Text className="fs-sm" ellipsis>
                  {field.value || '-'}
                </Typography.Text>
              </Row>
            );
          })
        }

        {renderLink()}
      </Fragment>
    );
  };

  return (
    <Fragment>
      <div className="pb-4 px-6 overflow-y-auto overflow-x-hidden">
        {renderCrmInfo()}
      </div>
    </Fragment>
  );
};

CrmIntegrationsPanel.propTypes = {
  conversation: PropTypes.object
};

export default CrmIntegrationsPanel;
